import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { isApiError, isTransientError } from '@/lib/errors';
import { apiGet } from '@/lib/http/client';

import { setOAuthInstanceRoutingCookie } from '../../oauth-instance-cookie';
import { getStripeAuthorizeUrl } from '../api/connect';

interface ConnectStripeButtonProps {
  orgId: number | string;
  label?: string;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
  disabled?: boolean;
  onError?: (reason: string) => void;
}

// Maps an authorize-request failure to a reason key understood by
// StripeSection's STRIPE_ERROR_MESSAGES map. Order matters: 503 is a 5xx, so it
// must be matched before the transient check below (a 503 config gate is not
// retryable, unlike a genuine 5xx wobble from the request itself).
function classifyAuthorizeError(err: unknown): string {
  if (isApiError(err)) {
    if (err.statusCode === 503) {
      return 'not_configured';
    }
    if (err.statusCode === 403) {
      return 'not_permitted';
    }
    if (err.statusCode === 409) {
      return 'integration_disabled';
    }
    // Any other ApiError (e.g. a 5xx) falls through to isTransientError below.
  }
  // Network failure, timeout, or a 5xx from the request itself — the only
  // case where inviting a retry is honest.
  if (isTransientError(err)) {
    return 'authorize_failed';
  }
  return 'unknown';
}

export function ConnectStripeButton({
  orgId,
  label,
  variant,
  size,
  disabled,
  onError,
}: ConnectStripeButtonProps) {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleClick = useCallback(async () => {
    setIsConnecting(true);
    try {
      // The authorize endpoint returns JSON ({ url: <Stripe App install-link URL> }) —
      // it does not redirect, so navigating the browser to the endpoint itself
      // would land on a raw JSON page. Fetch the URL, then navigate to Stripe.
      const { url } = await apiGet<{ url: string }>(
        getStripeAuthorizeUrl(orgId)
      );
      setOAuthInstanceRoutingCookie();
      window.location.href = url;
    } catch (err) {
      setIsConnecting(false);
      onError?.(classifyAuthorizeError(err));
    }
  }, [orgId, onError]);

  return (
    <Button
      color={variant === 'secondary' ? 'secondary' : 'primary'}
      size={size ?? 'md'}
      onClick={handleClick}
      isDisabled={disabled || isConnecting}
      isLoading={isConnecting}
      showTextWhileLoading
    >
      {label ?? 'Connect Stripe account'}
    </Button>
  );
}
